import { persistLinkedAccountLanguages } from "./account-linking.js";
import {
  isAuthenticatedSupabaseUser,
  normalizeOnboardingEmailAddress,
} from "./onboarding-auth.js";

const AUTH_CALLBACK_FALLBACK_MESSAGE = (
  "We couldn't complete that email link. Request a new email or enter the 6-digit code instead."
);
const STALE_LINK_AUTH_CALLBACK_MESSAGE = (
  "Open the latest email in the browser where you added your email, or sign in with this email."
);

function normalizeStatus(value) {
  return String(value || "").trim().toLowerCase() === "email-sent"
    ? "email-sent"
    : "idle";
}

function supabaseUserMatchesRecoveryEmail(user, expectedEmail = "") {
  const normalizedExpectedEmail = normalizeOnboardingEmailAddress(expectedEmail).toLowerCase();
  if (!normalizedExpectedEmail) {
    return false;
  }
  return normalizeOnboardingEmailAddress(user?.email).toLowerCase() === normalizedExpectedEmail;
}

export function resolveOnboardingAuthCallbackRecovery({
  authError = null,
  status = "",
  isSignInSurface = false,
  hasRecoverableOwnedAccount = false,
} = {}) {
  if (!authError) {
    return {
      branch: "none",
      errorTarget: null,
      message: "",
      shouldAutoRecover: false,
    };
  }

  if (!isSignInSurface && hasRecoverableOwnedAccount) {
    return {
      branch: "already_connected",
      errorTarget: null,
      message: "",
      shouldAutoRecover: true,
    };
  }

  return {
    branch: isSignInSurface ? "auth_error" : "stale_link",
    errorTarget: normalizeStatus(status) === "email-sent" ? "verification" : "form",
    message: isSignInSurface
      ? String(authError.message || "").trim() || AUTH_CALLBACK_FALLBACK_MESSAGE
      : STALE_LINK_AUTH_CALLBACK_MESSAGE,
    shouldAutoRecover: false,
  };
}

export async function recoverLinkedAuthCallbackAccount({
  client = null,
  languageStepComplete = false,
  handoffToken = "",
  currentAccountId = "",
  email = "",
  languagePayload = {},
  userMode = "",
  getCurrentSupabaseUser,
  persistLinkedAccountLanguagesImpl = persistLinkedAccountLanguages,
  adoptOnboardingAccount,
  saveAccountLanguages,
  setCurrentAccountId,
  clearOnboardingHandoffToken,
} = {}) {
  const normalizedHandoffToken = String(handoffToken || "").trim();
  if (
    !client
    || !languageStepComplete
    || String(userMode || "").trim().toLowerCase() !== "existing"
    || !normalizedHandoffToken
    || typeof getCurrentSupabaseUser !== "function"
  ) {
    return "";
  }

  const user = await getCurrentSupabaseUser(client);
  const normalizedEmail = normalizeOnboardingEmailAddress(email);
  if (
    !isAuthenticatedSupabaseUser(user)
    || !supabaseUserMatchesRecoveryEmail(user, normalizedEmail)
  ) {
    return "";
  }

  return String(await persistLinkedAccountLanguagesImpl({
    currentAccountId,
    handoffToken: normalizedHandoffToken,
    email: normalizedEmail,
    languagePayload,
    adoptOnboardingAccount,
    saveAccountLanguages,
    setCurrentAccountId,
    clearOnboardingHandoffToken,
  }) || "").trim();
}
